import { Component, DestroyRef, effect, inject, OnInit, untracked, ViewChild } from '@angular/core';
import { SitesTableComponent } from '../my-brand-screen/sites-table/sites-table.component';
import { NgIf } from '@angular/common';
import { PaginatorComponent } from '../../shared/components/paginator/paginator.component';
import { WidgetContainerComponent } from '../my-brand-screen/widget-container/widget-container.component';
import { ChartData } from '../../shared/types/common';
import { BrandsService } from '../../services/brands.service';
import { catchError, finalize, of, Subject, switchMap } from 'rxjs';
import {
  CompetitionFiltersResponse,
  CreativeGalleryResponse,
  CreativeGallerySortField,
  CREATIVE_GALLERY_SORT_OPTIONS,
  InventoryQualityTableColumns,
  InventoryQualityTablePayload,
  InventoryQualityTableResponse,
  CreativeGallerySort,
  ComparisonMetrics,
  ComingSoonMetrics,
  PerformanceComparisonMetric,
  PerformanceMapType,
  PerformanceMapMetrics,
  PerformanceMapResponse,
  CreativeGalleryPayload,
  PerformanceByTimeMetrics,
  PerformanceByDeviceMetrics,
  GalleryNavigation,
  SpendEfficiencyYAxisMetrics,
  SpendEfficiencyXAxisMetrics
} from '../my-brand-screen/types';
import {
  ContentCategoryDistributionPayload,
  IABTier1Category,
  MyCompetitionAudienceGenderBrand,
  MyCompetitionAudienceGenderPayload,
  MyCompetitionOverviewResponse,
  MyCompetitionPerformanceByTimeWidgetPayload,
  MyCompetitionPerformanceMapWidgetPayload,
  MyCompetitionPerformanceWidgetPayload,
  MyCompetitionPerformanceWidgetResponse,
  MyCompetitionShareOfVoiceWidgetPayload,
  MyCompetitionWidgetPayload,
  MyCompetitionWordCloudResponse,
  MyCompetitionPerformanceByTimeWidgetResponse,
  MyCompetitionPerformanceByDeviceWidgetPayload,
  MyCompetitionPerformanceByDeviceWidgetResponse,
  SpendEfficiencyResponse,
  MyCompetitionPerformanceMetrics,
} from './types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { PieChartComponent } from '../../shared/components/charts/content-category-breakdown/pie-chart.component';
import { HorizontalBarChartComponent } from '../../shared/components/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { PageEvent } from '@angular/material/paginator';
import { Format } from '../../services/util.service';
import { CreativeGalleryComponent } from '../my-brand-screen/creative-gallery/creative-gallery.component';
import { daysDataShowing, ROWS_PER_PAGE } from '../../utils/constants';
import { PosthogService } from '../../services/posthog.service';
import { PosthogEventName, PosthogEventCode } from '../../services/types';
import { PerformanceOverTimeComponent } from '../../shared/components/charts/performance-over-time/performance-over-time.component';
import { isSortField, SHARE_OF_VOICE_WIDGET_TITLE } from '../my-campaigns-screen/types';
import { TabsComponent } from '../../shared/components/tabs/tabs.component';
import { CompetitionPerformanceMapComponent } from './competition-performance-map/competition-performance-map.component';
import { defaultSortOptions, SortClickEvent, SortDirection, SortOptions } from '../../utils/sort-state';
import { WordCloudComponent } from '../../shared/components/word-cloud/word-cloud.component';
import { OverviewTilesComponent } from '../../shared/components/overview-tiles/overview-tiles.component';
import { DashboardFilters, DashboardService } from '../../services/dashboard.service';
import { DashboardType, TopBarWrapperComponent } from '../top-bar-wrapper/top-bar-wrapper.component';
import { ActivatedRoute } from '@angular/router';
import { SelectItem, SelectMode } from '../../shared/components/select-menu/select-menu.component';
import { ContentCategoryDistributionComponent } from './content-category-distribution/content-category-distribution.component';
import { ContentCategoryDistributionResponse } from './content-category-distribution/types/types';
import { NoWidgetDataComponent } from '../my-brand-screen/no-widget-data/no-widget-data.component';
import { PerformanceByTimeComponent } from './performance-by-time/performance-by-time.component';
import { TopLevelMetricsComponent } from '../brands-dashboard-screen/brands-dashboard/top-level-metrics/top-level-metrics.component';
import { PerformanceByDeviceComponent } from './performance-by-device/performance-by-device.component';
import { tooltips } from '../../utils/tooltips';
import { BrandsFiltersResolverService } from '../../services/brands-filters-resolver.service';
import { BrandLogoGraphComponent } from '../../shared/components/charts/brand-logo-graph/brand-logo-graph.component';
import { AuthorizationService } from '../../services/authorization.service';
import { DashboardWidget } from '../copilot/types';
import { DataTestIds } from '../../../../e2e/types';
import { AudienceGenderChartComponent } from './audience-gender/audience-gender-chart.component';

const CREATIVE_GALLERY_LIMIT = 8;

@Component({
  selector: 'app-my-competition-screen',
  imports: [
    NgIf,
    SitesTableComponent,
    PaginatorComponent,
    WidgetContainerComponent,
    NoWidgetDataComponent,
    PieChartComponent,
    HorizontalBarChartComponent,
    CreativeGalleryComponent,
    PerformanceOverTimeComponent,
    WordCloudComponent,
    OverviewTilesComponent,
    TabsComponent,
    CompetitionPerformanceMapComponent,
    TopBarWrapperComponent,
    ContentCategoryDistributionComponent,
    PerformanceByTimeComponent,
    PerformanceByDeviceComponent,
    TopLevelMetricsComponent,
    BrandLogoGraphComponent,
    AudienceGenderChartComponent,
  ],
  templateUrl: './my-competition-screen.component.html',
  styleUrl: './my-competition-screen.component.scss'
})
export class MyCompetitionScreenComponent implements OnInit {

  public audienceGenderData$ = new Subject<MyCompetitionAudienceGenderPayload>();
  public performanceByDeviceData$ = new Subject<MyCompetitionPerformanceByDeviceWidgetPayload>();
  public performanceByTimeData$ = new Subject<MyCompetitionPerformanceByTimeWidgetPayload>();
  public performanceOverTimeData$ = new Subject<MyCompetitionPerformanceWidgetPayload>();
  public performanceMapData$ = new Subject<MyCompetitionPerformanceMapWidgetPayload>();
  public shareOfVoiceData$ = new Subject<MyCompetitionShareOfVoiceWidgetPayload>();
  public comparisonData$ = new Subject<MyCompetitionWidgetPayload>();
  public competitorsInventoryQualityData$ = new Subject<InventoryQualityTablePayload>();
  public creativeGalleryData$ = new Subject<CreativeGalleryPayload>();
  public wordCloudData$ = new Subject<MyCompetitionWidgetPayload>();
  public overviewData$ = new Subject<MyCompetitionWidgetPayload>();
  public spendEfficiencyData$ = new Subject<MyCompetitionWidgetPayload>();
  public contentCategoryDistributionData$ = new Subject<MyCompetitionPerformanceMapWidgetPayload>();
  public loadingState = {
    performanceOverTime: true,
    performanceMap: true,
    shareOfVoice: true,
    comparison: true,
    inventoryQuality: true,
    creativeGallery: true,
    wordCloud: true,
    overview: true,
    contentCategoryDistribution: true,
    performanceByTime: true,
    performanceByDevice: true,
    spendEfficiency: true,
    audienceGender: true,
  };
  public shareOfVoiceData: ChartData[] = [];
  public performanceData: MyCompetitionPerformanceWidgetResponse | undefined;
  public performanceDataIsUnderDateThreshold: boolean = false;
  public comparisonData: ChartData[] = [];
  public myBrandCompareValue: Partial<ChartData> = {};
  public creativeGalleryData: CreativeGalleryResponse = { creativesData: [], total: 0 };
  public sortedComparisonColors: string[] = [];
  public sortedShareOfVoiceColors: string[] = [];
  public categoryOptions: SelectItem[] = [];
  public performanceMapData: PerformanceMapResponse = { myBrand: [], myCompetitors: [] };
  public performanceMapMetricOptions: SelectItem[] = PerformanceMapMetrics.filter(option => option.key !== 'impressions');
  public performanceMapSelectedOption: SelectItem = PerformanceMapMetrics[0];
  public performanceMapMetricControl: FormControl<string | null> = new FormControl(this.performanceMapSelectedOption.key);
  public performanceMapType: PerformanceMapType = PerformanceMapType.WORLDWIDE;
  public competitorsInventoryQualityData: InventoryQualityTableResponse | undefined;
  public inventoryQualityPageSize: number = 5;
  public inventoryQualityCurrentPage: number = 0;
  public creativeGalleryCurrentPage: number = 0;
  public initialPage: number = 1;
  public comparisonFormat: Format = 'shortPercentage';
  public performanceOverTimeFormat: Format = 'fractionPercentage';
  public gallerySortOptions: SelectItem<CreativeGallerySort>[] = CREATIVE_GALLERY_SORT_OPTIONS;
  public performanceMetricOptions: SelectItem[] = MyCompetitionPerformanceMetrics;
  public comparisonMetricOptions: SelectItem[] = [...ComparisonMetrics, ...ComingSoonMetrics];
  public spendEfficiencyYAxisOptions: SelectItem[] = SpendEfficiencyYAxisMetrics;
  public spendEfficiencyXAxisOptions: SelectItem[] = SpendEfficiencyXAxisMetrics;
  public spendEfficiencyYAxisControl: FormControl<string | null> = new FormControl(SpendEfficiencyYAxisMetrics[0].key);
  public spendEfficiencyXAxisControl: FormControl<string | null> = new FormControl(SpendEfficiencyXAxisMetrics[0].key);
  public wordCloudData: MyCompetitionWordCloudResponse = { brands: [] };
  public wordCloudContainerHeight = '435px';
  public wordCloudChartWidthMap = {};
  public overview?: MyCompetitionOverviewResponse;
  public spendEfficiencyData?: SpendEfficiencyResponse;
  public gallerySort: FormControl<{ sortField: CreativeGallerySortField, isAsc: boolean }> = new FormControl();
  public performanceMetricControl: FormControl<string | null> = new FormControl(MyCompetitionPerformanceMetrics[0].key);
  public comparisonMetricControl: FormControl<string | null> = new FormControl(ComparisonMetrics[0].key);
  public contentCategoryDistributionData: ContentCategoryDistributionResponse | undefined;
  public shareOfVoiceCategoryControl: FormControl<IABTier1Category[]> = new FormControl<IABTier1Category[]>([], { nonNullable: true });
  public shareOfVoiceCategoriesOptions: SelectItem<IABTier1Category>[] = this.getShareOfVoiceCategoryOptions();

  public performanceByTimeData?: MyCompetitionPerformanceByTimeWidgetResponse = { brands: [], isUnderDateThreshold: false };
  public performanceByTimeOptions: SelectItem[] = PerformanceByTimeMetrics;
  public performanceByTimeSelectedMetric: SelectItem = PerformanceByTimeMetrics[0];
  public performanceByTimeMetricControl: FormControl<string | null> = new FormControl(this.performanceByTimeSelectedMetric.key);

  public audienceGenderData: MyCompetitionAudienceGenderBrand[] = [];

  public performanceByDeviceData?: MyCompetitionPerformanceByDeviceWidgetResponse = { brands: [], isInDeviceBreakdown: false };
  public performanceByDeviceOptions: SelectItem[] = PerformanceByDeviceMetrics;
  public performanceByDeviceSelectedMetric: SelectItem = PerformanceByDeviceMetrics[0];
  public performanceByDeviceMetricControl: FormControl<string | null> = new FormControl(this.performanceByDeviceSelectedMetric.key);

  private pendingGalleryModalNav?: GalleryNavigation;
  private filters: CompetitionFiltersResponse = { brands: {} };
  private route = inject(ActivatedRoute);
  private brandsService = inject(BrandsService);
  private dashboardService = inject(DashboardService);
  private destroyRef = inject(DestroyRef);
  private posthogService = inject(PosthogService);
  private filtersResolverService = inject(BrandsFiltersResolverService);
  private authorizationService = inject(AuthorizationService);
  private inventoryQualitySortOptions: SortOptions = {
    sortField: InventoryQualityTableColumns.BRAND_IMPRESSIONS,
    isAsc: false
  };
  protected DashboardType = DashboardType;

  protected performanceMapVolumeThreshold: number | null = null;
  protected creativeGalleryVolumeThreshold: number | null = null;
  protected performanceByTimeVolumeThreshold: number | null = null;

  protected readonly tooltips = tooltips.myCompetition;
  protected readonly daysDataShowing = daysDataShowing;
  protected readonly rowsPerPage = ROWS_PER_PAGE;
  protected readonly CREATIVE_GALLERY_LIMIT = CREATIVE_GALLERY_LIMIT;
  protected readonly DataTestIds: typeof DataTestIds = DataTestIds;

  protected myBrand = this.dashboardService.myBrand;
  protected myCompetitors = this.dashboardService.myCompetitors;
  protected dashboardFilters = this.dashboardService.brandsFilters;
  protected brandColorsMap = this.dashboardService.brandColorsMap;
  protected brandLineColorsMap = this.dashboardService.brandLineColorsMap;
  protected brandColorsByNameMap = this.dashboardService.brandColorsByNameMap;
  protected readonly isTrialUser = this.authorizationService.isTrialUser;

  protected contentCategoryHeight: string = '386px';
  protected readonly DashboardWidget = DashboardWidget;

  @ViewChild('inventoryQualityPaginator') inventoryQualityPaginator!: PaginatorComponent;
  @ViewChild('creativeGalleryPaginator') creativeGalleryPaginator!: PaginatorComponent;
  @ViewChild(CreativeGalleryComponent) creativeGalleryCmp!: CreativeGalleryComponent;

  constructor() {
    effect(() => {
      const { brandsOptions, brandColorsMap, brandColorsByNameMap } = this.dashboardService;
      untracked(brandsOptions);
      untracked(brandColorsMap);
      untracked(brandColorsByNameMap);
      this.onFiltersChange(this.dashboardFilters());
    });
  }

  ngOnInit() {
    try {
      this.filters = this.route.snapshot.data['filters']['brands'] ?? { brands: {} };
    } catch (error) { }
    this.initializeBrandData();
  }

  private updateFilters(): void {
    this.filtersResolverService.resolve().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(filters => {
      this.filters = filters.brands;
    })
  }

  private initializeBrandData(): void {
    this.setComparisonSortedColors();
    this.setShareOfVoiceSortedColors();
    this.initPerformanceOverTimeSubject();
    this.initPerformanceMapSubject();
    this.initOverviewSubject();
    this.initShareOfVoiceSubject();
    this.initComparisonSubject();
    this.initInventoryQualitySubject();
    this.initCreativeGallerySubject();

    this.initContentCategorySubject();
    this.initPerformanceByTimeSubject();
    this.initPerformanceByDeviceSubject();
    this.initSpendEfficiencySubject();
    this.initAudienceGenderSubject();
    if (!this.isTrialUser) {
      this.initWordCloudSubject();
    }
  }

  public onFiltersChange(dashboardFilters: DashboardFilters): void {
    this.updateFilters();
    this.resetInventoryQuality();
    this.creativeGalleryPaginator?.goToPage(1);
    this.setFiltersOptions(dashboardFilters);
    this.initializeAllData();
  }

  private resetInventoryQuality(): void {
    this.inventoryQualityPaginator?.goToPage(1);
  }

  public onInventoryQualityPageChange(event: PageEvent, toTrack = true): void {
    if (toTrack) {
      this.posthogService.capture(PosthogEventName.INVENTORY_QUALITY_TABLE_PAGINATION, {
        pageNumber: event.pageIndex + 1,
        lastPageNumber: Math.ceil(event.length / event.pageSize),
        [PosthogEventCode.WIDGET_NAME]: 'inventory_quality_table'

      });
    }
    this.inventoryQualityCurrentPage = event.pageIndex as number;
    const offset = this.inventoryQualityCurrentPage * this.inventoryQualityPageSize;
    this.competitorsInventoryQualityData$.next(this.getInventoryQualityPayload(this.dashboardFilters(), offset));
  }

  public onSortFieldChange(): void {
    this.creativeGalleryPaginator?.goToPage(1);
    this.onCreativeGalleryPageChange({
      pageIndex: 0,
      pageSize: CREATIVE_GALLERY_LIMIT,
      length: this.creativeGalleryData.total,
      previousPageIndex: this.creativeGalleryCurrentPage
    });
  }

  public onCreativeGalleryPageChange(event: PageEvent): void {
    this.creativeGalleryCurrentPage = event.pageIndex as number;
    this.getSortedCreativeGallery();
  }

  public onInventoryQualityRowsChange(rowsPerPage: number | null): void {
    if (!rowsPerPage) return;
    this.inventoryQualityPageSize = rowsPerPage;
    this.inventoryQualityPaginator?.goToPage(1);
    this.onInventoryQualityPageChange({
      pageIndex: 0,
      pageSize: this.inventoryQualityPageSize,
      length: this.competitorsInventoryQualityData?.total || 0,
      previousPageIndex: this.inventoryQualityCurrentPage
    }, false);
  }

  public onPerformanceMetricChange(): void {
    this.performanceOverTimeData$.next(this.getPerformanceWidgetPayload(this.dashboardFilters()));
    this.posthogService.capture(PosthogEventName.METRIC_SELECTED, { [PosthogEventCode.METRIC_NAME]: this.performanceMetricControl.value, [PosthogEventCode.WIDGET_NAME]: 'performance_over_time' });
  }

  public onPerformanceMapMetricChange() {
    this.performanceMapSelectedOption = this.performanceMapMetricOptions.find(option => option.key === this.performanceMapMetricControl.value) || PerformanceMapMetrics[0];
    this.posthogService.capture(PosthogEventName.METRIC_SELECTED, { [PosthogEventCode.METRIC_NAME]: this.performanceMapMetricControl.value, [PosthogEventCode.WIDGET_NAME]: 'performance_map' });
  }

  public onComparisonMetricChange(): void {
    this.comparisonData$.next(this.getComparisonWidgetPayload(this.dashboardFilters()));
    this.posthogService.capture(PosthogEventName.METRIC_SELECTED, { [PosthogEventCode.WIDGET_NAME]: 'performance_comparison', [PosthogEventCode.METRIC_NAME]: this.comparisonMetricControl.value });
  }

  public onPerformanceByTimeMetricChange(): void {
    this.performanceByTimeSelectedMetric = this.performanceByTimeOptions.find(option => option.key === this.performanceByTimeMetricControl.value) || PerformanceByTimeMetrics[0];
    this.posthogService.capture(PosthogEventName.METRIC_SELECTED, { [PosthogEventCode.METRIC_NAME]: this.performanceByTimeMetricControl.value, [PosthogEventCode.WIDGET_NAME]: 'performance_by_time' });
  }

  public onPerformanceByDeviceMetricChange(): void {
    this.performanceByDeviceSelectedMetric = this.performanceByDeviceOptions.find(option => option.key === this.performanceByDeviceMetricControl.value) || PerformanceByDeviceMetrics[0];
    this.posthogService.capture(PosthogEventName.METRIC_SELECTED, { [PosthogEventCode.METRIC_NAME]: this.performanceByDeviceMetricControl.value, [PosthogEventCode.WIDGET_NAME]: 'performance_by_device' });
  }

  public onSpendEfficiencyMetricChange(): void {
    this.posthogService.capture(PosthogEventName.METRIC_SELECTED, {
      [PosthogEventCode.METRIC_NAME]: `${this.spendEfficiencyXAxisControl.value}, ${this.spendEfficiencyYAxisControl.value}`,
      [PosthogEventCode.WIDGET_NAME]: 'spend_efficiency_graph'
    });
  }

  get spendEfficiencySelectedMetrics(): string[] {
    const metrics: string[] = [];
    if (this.spendEfficiencyXAxisControl.value) {
      metrics.push(this.spendEfficiencyXAxisControl.value);
    }
    if (this.spendEfficiencyYAxisControl.value) {
      metrics.push(this.spendEfficiencyYAxisControl.value);
    }
    return metrics;
  }


  protected onPerformanceMapVolumeFilter(threshold: number | null): void {
    this.performanceMapVolumeThreshold = threshold;
    this.performanceMapData$.next(this.getPerformanceMapWidgetPayload(this.dashboardFilters()));
    this.posthogService.captureVolumeFilter('performance_map', threshold);
  }

  protected onCreativeGalleryVolumeFilter(threshold: number | null): void {
    this.creativeGalleryVolumeThreshold = threshold;
    this.creativeGalleryData$.next(this.getCreativeGalleryPayload(this.dashboardFilters()));
    this.creativeGalleryPaginator?.goToPage(1);
    this.posthogService.captureVolumeFilter('creative_gallery', threshold);
  }

  protected onPerformanceByTimeVolumeFilter(threshold: number | null): void {
    this.performanceByTimeVolumeThreshold = threshold;
    this.performanceByTimeData$.next(this.getPerformanceByTimeWidgetPayload(this.dashboardFilters()));
    this.posthogService.captureVolumeFilter('hourly_breakdown', threshold);
  }

  public switchPerformanceMapTab(tab: string): void {
    this.performanceMapType = tab.toLowerCase() === PerformanceMapType.US ? PerformanceMapType.US : PerformanceMapType.WORLDWIDE;
    this.performanceMapData$.next(this.getPerformanceMapWidgetPayload(this.dashboardFilters()));
    this.posthogService.capture(PosthogEventName.MAP_REGION_SELECTED, { [PosthogEventCode.MAR_REGION]: this.performanceMapType, [PosthogEventCode.WIDGET_NAME]: 'performance_map' });
  }

  public onCreativeModalEdgeNav(direction: GalleryNavigation) {
    const total = this.creativeGalleryData.total;
    const pageSize = this.CREATIVE_GALLERY_LIMIT;
    const lastPageIndex = Math.max(0, Math.ceil(total / pageSize) - 1);
    const targetIndex = direction === GalleryNavigation.NEXT ? this.creativeGalleryCurrentPage + 1 : this.creativeGalleryCurrentPage - 1;
    if (targetIndex < 0) {
      this.pendingGalleryModalNav = direction;
      this.creativeGalleryPaginator?.goToPage(lastPageIndex + 1);
      return;
    }
    if (targetIndex > lastPageIndex) {
      this.pendingGalleryModalNav = direction;
      this.creativeGalleryPaginator?.goToPage(1);
      return;
    }
    this.pendingGalleryModalNav = direction;
    this.creativeGalleryPaginator?.goToPage(targetIndex + 1);
  }

  private initializeAllData(): void {
    const dashboardFilters = this.dashboardFilters();
    this.spendEfficiencyData$.next(this.getWidgetPayload(dashboardFilters));
    this.performanceByTimeData$.next(this.getPerformanceByTimeWidgetPayload(dashboardFilters));
    this.performanceByDeviceData$.next(this.getPerformanceByDeviceWidgetPayload(dashboardFilters));
    this.performanceOverTimeData$.next(this.getPerformanceWidgetPayload(dashboardFilters));
    this.performanceMapData$.next(this.getPerformanceMapWidgetPayload(dashboardFilters));
    this.overviewData$.next(this.getOverviewWidgetPayload(dashboardFilters));
    this.shareOfVoiceData$.next(this.getShareOfVoiceWidgetPayload(dashboardFilters));
    this.comparisonData$.next(this.getComparisonWidgetPayload(dashboardFilters));
    this.competitorsInventoryQualityData$.next(this.getInventoryQualityPayload(dashboardFilters));
    this.creativeGalleryData$.next(this.getCreativeGalleryPayload(dashboardFilters));
    if (!this.isTrialUser) {
      this.wordCloudData$.next(this.getWidgetPayload(dashboardFilters));
    }
    this.contentCategoryDistributionData$.next(this.getContentCategoryDistributionPayload(dashboardFilters));
    this.audienceGenderData$.next(this.getAudienceGenderPayload(dashboardFilters));
  }

  public onShareOfVoiceCategoryChange(): void {
    this.shareOfVoiceData$.next(this.getShareOfVoiceWidgetPayload(this.dashboardFilters()));
    this.posthogService.capture(PosthogEventName.FILTERED_CONTENT_CATEGORY, {
      [PosthogEventCode.FILTERED_CONTENT_CATEGORY]: this.shareOfVoiceCategoryControl.value.join(';'),
      [PosthogEventCode.WIDGET_NAME]: 'share_of_voice'
    });
  }
  private handleWordCloudData(wordCloudData?: MyCompetitionWordCloudResponse): void {
    if (wordCloudData) {
      this.wordCloudContainerHeight = (wordCloudData?.brands?.length || 0) > 3 ? '800px' : '435px';
      this.wordCloudChartWidthMap = this.getWordCloudChartWidthMap(wordCloudData);
      this.wordCloudData = wordCloudData;
    }
  }

  private initPerformanceMapSubject(): void {
    this.performanceMapData$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(payload => {
        this.loadingState.performanceMap = true;
        const observable = (this.performanceMapType === PerformanceMapType.US) ?
          this.brandsService.getCompetitorsPerformanceMapUsa(payload) :
          this.brandsService.getCompetitorsPerformanceMapWorldwide(payload);
        return observable.pipe(
          finalize(() => {
            this.loadingState.performanceMap = false
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      this.handlePerformanceMapData(this.dashboardFilters(), result);
    });
  }

  private initPerformanceOverTimeSubject(): void {
    this.performanceOverTimeData$.pipe(
      switchMap(payload => {
        this.loadingState.performanceOverTime = true;
        return this.brandsService.getPerformanceData(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.performanceOverTime = false
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      this.handlePerformanceChartData(this.dashboardFilters(), result);
      this.performanceOverTimeFormat = this.getPerformanceOverTimeFormat();
    });
  }

  private initContentCategorySubject(): void {
    this.contentCategoryDistributionData$.pipe(
      switchMap(payload => {
        this.loadingState.contentCategoryDistribution = true;
        return this.brandsService.getContentCategoryDistribution(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.contentCategoryDistribution = false
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      this.contentCategoryDistributionData = result;
    });
  }

  private initPerformanceByTimeSubject(): void {
    this.performanceByTimeData$.pipe(
      switchMap(payload => {
        this.loadingState.performanceByTime = true;
        return this.brandsService.getPerformanceByTime(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.performanceByTime = false
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      this.performanceByTimeData = result;
    });
  }

  private initPerformanceByDeviceSubject(): void {
    this.performanceByDeviceData$.pipe(
      switchMap(payload => {
        this.loadingState.performanceByDevice = true;
        return this.brandsService.getPerformanceByDevice(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.performanceByDevice = false
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      this.performanceByDeviceData = result;
    });
  }

  private initOverviewSubject(): void {
    this.overviewData$.pipe(
      switchMap(payload => {
        this.loadingState.overview = true;
        return this.brandsService.getCompetitorsOverview(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.overview = false
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      this.overview = result;
    });
  }

  private initSpendEfficiencySubject(): void {
    this.spendEfficiencyData$.pipe(
      switchMap(payload => {
        this.loadingState.spendEfficiency = true;
        return this.brandsService.getSpendEfficiency(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.spendEfficiency = false
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      this.spendEfficiencyData = result;
    });
  }

  private initAudienceGenderSubject(): void {
    this.audienceGenderData$.pipe(
      switchMap(payload => {
        this.loadingState.audienceGender = true;
        return this.brandsService.getMyCompetitionAudienceGender(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => { this.loadingState.audienceGender = false; }),
          catchError(() => of(undefined)),
        );
      })
    ).subscribe(result => {
      this.audienceGenderData = [...(result?.brands ?? [])].reverse();
    });
  }

  private getAudienceGenderPayload(dashboardFilters: DashboardFilters): MyCompetitionAudienceGenderPayload {
    return {
      brandKey: dashboardFilters.myBrand.key,
      competitorKeys: dashboardFilters.myCompetitors.map(c => c.key),
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters))
    };
  }

  private initShareOfVoiceSubject(): void {
    this.shareOfVoiceData$.pipe(
      switchMap(payload => {
        this.loadingState.shareOfVoice = true;
        return this.brandsService.getShareOfVoice(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.shareOfVoice = false
          }),
          catchError((error: any) => {
            this.shareOfVoiceData = [];
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      if (result) {
        this.shareOfVoiceData = result.brands
          .sort((a, b) => (a.key === this.dashboardFilters().myBrand.key ? -1 : b.key === this.dashboardFilters().myBrand.key ? 1 : 0));
        this.setShareOfVoiceSortedColors();
      }
    });
  }

  private initComparisonSubject(): void {
    this.comparisonData$.pipe(
      switchMap(payload => {
        this.loadingState.comparison = true;
        return this.brandsService.getComparison(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.comparison = false
          }),
          catchError((error: any) => {
            this.comparisonData = [];
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      if (result) {
        const brandKey = this.dashboardFilters().myBrand.key;
        const brandName = this.dashboardFilters().myBrand.label;
        this.myBrandCompareValue = result.brands.find(item => item.key === brandKey) ?? {key: brandKey, name: brandName, score:0};
        this.comparisonData = result.brands.filter(item => item.key !== brandKey);
        this.comparisonFormat = this.getComparisonChartFormat();
        this.setComparisonSortedColors();
      }
    });
  }

  private initInventoryQualitySubject(): void {
    this.competitorsInventoryQualityData$.pipe(
      switchMap(payload => {
        this.loadingState.inventoryQuality = true;
        return this.brandsService.getMyCompetitionInventoryQuality(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.inventoryQuality = false
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      if (result) {
        this.buildInventoryQualityTableData(result);
      }
    });
  }

  private initCreativeGallerySubject(): void {
    this.creativeGalleryData$.pipe(
      switchMap(payload => {
        this.loadingState.creativeGallery = true;
        return this.brandsService.getCompetitorsCreativeGallery(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.creativeGallery = false
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      if (result) {
        this.creativeGalleryData = result;
      }
      if (this.pendingGalleryModalNav && this.creativeGalleryData.creativesData.length) {
        const openFirst = this.pendingGalleryModalNav === GalleryNavigation.NEXT;
        const target = openFirst ? this.creativeGalleryData.creativesData[0] : this.creativeGalleryData.creativesData[this.creativeGalleryData.creativesData.length - 1];
        this.pendingGalleryModalNav = undefined;
        setTimeout(() => this.creativeGalleryCmp.openCardModal(target.id, 'navigate'), 0);
      }
    });
  }

  private initWordCloudSubject(): void {
    this.wordCloudData$.pipe(
      switchMap(payload => {
        this.loadingState.wordCloud = true;
        return this.brandsService.getMyCompetitionWordCloud(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loadingState.wordCloud = false
          }),
          catchError((error: any) => {
            return of(undefined);
          }),
        );
      })
    ).subscribe(result => {
      this.handleWordCloudData(result);
    });
  }

  private getWordCloudChartWidthMap(wordCloudData: MyCompetitionWordCloudResponse): Record<string, number> {
    const map: Record<string, number> = {};

    const len = wordCloudData.brands?.length || 0;
    const narrow = 342;
    const wide = 536;

    wordCloudData.brands.forEach((brand, index) => {
      switch (len) {
        case 1:
          map[brand.brandKey] = 1116;
          break;
        case 2:
        case 4:
          map[brand.brandKey] = wide;
          break;
        case 3:
        case 6:
          map[brand.brandKey] = narrow;
          break;
        case 5:
          map[brand.brandKey] = index > 2 ? wide : narrow;
      }
    });
    return map;
  }

  private handlePerformanceChartData(dashboardFilters: DashboardFilters, performanceData?: MyCompetitionPerformanceWidgetResponse): void {
    this.performanceDataIsUnderDateThreshold = performanceData?.isUnderDateThreshold || false;

    if (!performanceData || (!performanceData.myBrand && !performanceData.myCompetitors)) {
      this.performanceData = undefined;
      return;
    }
    performanceData.myBrand.brandName = dashboardFilters.myBrand.label;
    if (performanceData.myCompetitors){
      performanceData.myCompetitors.forEach(item => {
        item.brandName = dashboardFilters.myCompetitors.find(option => option.key === item.brandKey)?.label;
      })
    }
    this.performanceData = performanceData;
  }

  private handlePerformanceMapData(dashboardFilters: DashboardFilters, performanceMapData?: PerformanceMapResponse): void {
    if (!performanceMapData || !performanceMapData.myBrand || !performanceMapData.myCompetitors) {
      this.performanceMapData = {};
      return;
    }
    performanceMapData.myBrand.forEach(item => {
      item.brand = dashboardFilters.myBrand.label;
    });
    performanceMapData.myCompetitors.forEach(item => {
      item.brand = dashboardFilters.myCompetitors.find(option => option.key === item.brand)?.label as string;
    });
    this.performanceMapData = performanceMapData;
  }

  private getComparisonChartFormat(): Format {
    switch (this.comparisonMetricControl.value) {
      case PerformanceComparisonMetric.ATTENTION:
        return 'shortPercentage';
      case PerformanceComparisonMetric.VIEWABILITY:
        return 'shortPercentage';
      case PerformanceComparisonMetric.DWELL_TIME:
        return 'seconds';
      case PerformanceComparisonMetric.CPM:
        return 'currency';
      case PerformanceComparisonMetric.AD_CLUTTER:
        return 'shortNumber';
    }
    return 'shortNumber';
  }

  public getPerformanceOverTimeFormat(): Format {
    switch (this.performanceMetricControl.value) {
      case PerformanceComparisonMetric.SHARE_OF_VOICE:
        return 'fractionPercentage';
      case PerformanceComparisonMetric.DWELL_TIME:
        return 'seconds';
      case PerformanceComparisonMetric.CPM:
        return 'currency';
      case PerformanceComparisonMetric.SPEND:
        return 'shortCurrencyNumber';
      case PerformanceComparisonMetric.ATTENTION:
        return 'shortPercentage';
      case PerformanceComparisonMetric.EFFICIENCY:
      case PerformanceComparisonMetric.IMPACT:
      case PerformanceComparisonMetric.IMPRESSIONS:
        return 'shortNumber';
    }
    return 'fractionPercentage';
  }

  private setFiltersOptions(dashboardFilters: DashboardFilters): void {
    const creativeGalleryFilterKey = this.gallerySortOptions[0].key;
    this.gallerySort.setValue(creativeGalleryFilterKey);
  }

  private getCreativeGalleryPayload(dashboardFilters: DashboardFilters): CreativeGalleryPayload {
    const competitorsKeys = dashboardFilters.myCompetitors.map(c => c.key);
    return {
      brandsKeys: competitorsKeys,
      sortField: 'impressions',
      isAsc: false,
      limit: CREATIVE_GALLERY_LIMIT,
      offset: 0,
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters)),
      ...(this.creativeGalleryVolumeThreshold ? { minImpressions: this.creativeGalleryVolumeThreshold } : {})
    };
  }

  private getComparisonWidgetPayload(dashboardFilters: DashboardFilters): MyCompetitionWidgetPayload {
    return {
      metric: this.comparisonMetricControl.value as string,
      brandKey: dashboardFilters.myBrand.key,
      competitorKeys: dashboardFilters.myCompetitors.map(c => c.key),
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters))
    };
  }

  private getWidgetPayload(dashboardFilters: DashboardFilters): MyCompetitionWidgetPayload {
    return {
      brandKey: dashboardFilters.myBrand.key,
      competitorKeys: dashboardFilters.myCompetitors.map(c => c.key),
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters))
    };
  }

  private getShareOfVoiceWidgetPayload(dashboardFilters: DashboardFilters): MyCompetitionShareOfVoiceWidgetPayload {
    return {
      brandKey: dashboardFilters.myBrand.key,
      competitorKeys: dashboardFilters.myCompetitors.map(c => c.key),
      categories: this.shareOfVoiceCategoryControl.value,
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters))
    };
  }

  private getPerformanceMapWidgetPayload(dashboardFilters: DashboardFilters): MyCompetitionPerformanceMapWidgetPayload {
    return {
      brandKey: dashboardFilters.myBrand.key,
      competitorsKeys: dashboardFilters.myCompetitors.map(c => c.key),
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters)),
      ...(this.performanceMapVolumeThreshold ? { minImpressions: this.performanceMapVolumeThreshold } : {})
    };
  }

  private getContentCategoryDistributionPayload(dashboardFilters: DashboardFilters): ContentCategoryDistributionPayload {
    return {
      brandKey: dashboardFilters.myBrand.key,
      competitorsKeys: dashboardFilters.myCompetitors.map(c => c.key),
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters))
    };
  }

  private getPerformanceByTimeWidgetPayload(dashboardFilters: DashboardFilters): MyCompetitionPerformanceByTimeWidgetPayload {
    return {
      brandKey: dashboardFilters.myBrand.key,
      competitorKeys: dashboardFilters.myCompetitors.map(c => c.key),
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters)),
      ...(this.performanceByTimeVolumeThreshold ? { minImpressions: this.performanceByTimeVolumeThreshold } : {})
    };
  }

  private getPerformanceByDeviceWidgetPayload(dashboardFilters: DashboardFilters): MyCompetitionPerformanceByDeviceWidgetPayload {
    return {
      brandKey: dashboardFilters.myBrand.key,
      competitorKeys: dashboardFilters.myCompetitors.map(c => c.key),
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters))
    };
  }

  private getPerformanceWidgetPayload(dashboardFilters: DashboardFilters): MyCompetitionPerformanceWidgetPayload {
    return {
      brandKey: dashboardFilters.myBrand.key,
      competitorKeys: dashboardFilters.myCompetitors.map(c => c.key),
      metric: this.performanceMetricControl.value as string,
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters))
    };
  }

  private getInventoryQualityPayload(dashboardFilters: DashboardFilters, offset?: number): InventoryQualityTablePayload {
    return {
      brands: dashboardFilters.myCompetitors.map(c => c.key),
      categories: [], // TODO: remove categories after server support
      sortField: InventoryQualityTableColumns.BRAND_IMPRESSIONS,
      limit: this.inventoryQualityPageSize,
      isAsc: false,
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters)),
      ...(offset ? { offset } : {}),
      ...(this.inventoryQualitySortOptions)
    }
  }

  private getOverviewWidgetPayload(dashboardFilters: DashboardFilters): MyCompetitionWidgetPayload {
    return {
      brandKey: dashboardFilters.myBrand.key,
      competitorKeys: dashboardFilters.myCompetitors.map(c => c.key),
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters))
    };
  }

  private buildInventoryQualityTableData(data: InventoryQualityTableResponse): void {
    this.competitorsInventoryQualityData = data;
  }

  private getSortedCreativeGallery() {
    const dashboardFilters = this.dashboardFilters();
    this.creativeGalleryData$.next({
      brandsKeys: dashboardFilters.myCompetitors.map(c => c.key),
      limit: CREATIVE_GALLERY_LIMIT,
      offset: this.creativeGalleryCurrentPage * CREATIVE_GALLERY_LIMIT,
      ...this.gallerySort.value,
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters)),
      ...(this.creativeGalleryVolumeThreshold ? { minImpressions: this.creativeGalleryVolumeThreshold } : {})
    });
  }

  private setComparisonSortedColors(): void {
    this.brandsService.sortChartData(this.comparisonData);
    this.sortedComparisonColors = this.comparisonData.map(item => this.brandColorsMap()[item.key as string]);
  }

  private setShareOfVoiceSortedColors(): void {
    this.brandsService.sortChartData(this.shareOfVoiceData);
    this.sortedShareOfVoiceColors = this.shareOfVoiceData.map(item => this.brandColorsMap()[item.key as string]);
  }

  private getShareOfVoiceCategoryOptions(): SelectItem<IABTier1Category>[] {
    return Object.values(IABTier1Category).map((category) => ({
      key: category,
      label: category
    }))
  }

  protected onSortInventoryQualityTable(event: SortClickEvent): void {
    if (event.direction !== SortDirection.default && isSortField(event.field)) {
      this.inventoryQualitySortOptions = {
        sortField: event.field,
        isAsc: event.direction === SortDirection.asc,
      }
    } else {
      this.inventoryQualitySortOptions = defaultSortOptions;
    }

    this.inventoryQualityPaginator?.goToPage(1);
    this.onInventoryQualityPageChange({
      pageIndex: 0,
      pageSize: this.inventoryQualityPageSize,
      length: this.competitorsInventoryQualityData?.total || 0,
      previousPageIndex: this.inventoryQualityCurrentPage,
    }, false);
  }

  protected compareOptions(option1: CreativeGallerySort, option2: CreativeGallerySort): boolean {
    return option1 && option2 && option1.sortField === option2.sortField && option1.isAsc === option2.isAsc;
  }

  protected getBrandsKeys(): { brandKey: string; competitorsKeys: string[] } {
    return {
      brandKey: this.dashboardFilters().myBrand.key,
      competitorsKeys: this.dashboardFilters().myCompetitors.map(c => c.key)
    };
  }

  protected onContentCategoryListToggle(state: "collapse" | "expand"): void {
    this.contentCategoryHeight = state === "collapse" ? '386px' : '916px';
  }

  protected readonly SelectMode = SelectMode;
  protected readonly SHARE_OF_VOICE_WIDGET_TITLE = SHARE_OF_VOICE_WIDGET_TITLE;
}
